import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { EventListItemDTO } from "../../shared/dto";
import { EVENT_REGIONS, regionLabel } from "../../shared/enums";
import { uploadsUrl } from "../lib/uploader";
import { formatEventDate } from "../lib/format";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  EmptyState,
} from "../components/ui";

const MODE_LABEL: Record<string, string> = {
  offline: "来場",
  online: "オンライン",
  hybrid: "ハイブリッド",
};

export function EventsPage() {
  const [items, setItems] = useState<EventListItemDTO[]>([]);
  const [tab, setTab] = useState<"active" | "past">("active");
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = `status=${tab}${region ? `&region=${encodeURIComponent(region)}` : ""}`;
    api
      .get<{ items: EventListItemDTO[] }>(`/events?${qs}`)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, [tab, region]);

  // F-R-c: ピル形タブトグル (画像準拠)
  const tabs = (
    <div
      role="tablist"
      aria-label="イベント絞り込み"
      className="inline-flex gap-3"
    >
      {(["active", "past"] as const).map((t) => {
        const selected = tab === t;
        return (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`text-sm px-6 py-2.5 rounded-full font-semibold transition-colors ${
              selected
                ? "bg-tora text-white shadow-sm"
                : "bg-white border border-tora-ink/20 text-tora-ink/60 hover:border-tora hover:text-tora"
            }`}
            onClick={() => setTab(t)}
          >
            {t === "active" ? "開催予定" : "終了済み"}
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      <SectionHeading level={2} action={tabs}>
        イベント
      </SectionHeading>
      <div className="mb-3">
        <Link
          to="/events/mine"
          className="tap-target gap-1 text-sm text-tora font-semibold hover:underline"
        >
          🎫 参加予定のイベントを見る →
        </Link>
      </div>
      {/* 地域フィルタ */}
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="地域で絞り込み">
        {[{ value: "", label: "すべて" }, ...EVENT_REGIONS].map((r) => {
          const selected = region === r.value;
          return (
            <button
              key={r.value || "all"}
              type="button"
              onClick={() => setRegion(r.value)}
              className={`inline-flex items-center text-xs px-4 py-1.5 [@media(pointer:coarse)]:min-h-[40px] rounded-full font-semibold transition-colors ${
                selected
                  ? "bg-tora text-white"
                  : "bg-white border border-tora-ink/20 text-tora-ink/60 hover:border-tora hover:text-tora"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          icon="🗓"
          title={
            tab === "active"
              ? "開催予定のイベントはありません"
              : "過去のイベントはありません"
          }
        />
      ) : (
        // F-R-c: 横型ビッグカード (画像準拠) / 1 列で余白たっぷり
        <ul className="space-y-5 md:space-y-6">
          {items.map((e) => {
            const full = e.remainingCapacity === 0;
            const { date, time } = formatEventDate(e.eventStartAt);
            const thumb = uploadsUrl(e.thumbnailKey);
            return (
              <li key={e.id}>
                <Link
                  to={`/events/${e.id}`}
                  className="card p-5 md:p-7 flex flex-col md:flex-row gap-5 md:gap-7 group transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tora"
                >
                  {/* サムネ (左、大きめ) */}
                  <div className="relative w-full md:w-48 lg:w-56 aspect-[4/3] md:aspect-[4/3] rounded-xl bg-black/5 overflow-hidden flex-shrink-0">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="w-full h-full object-contain md:group-hover:scale-[1.03] transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-tora-ink/30">
                        <span className="text-5xl" aria-hidden="true">
                          🗓
                        </span>
                        <span className="mt-1 text-[10px] tracking-widest text-tora-ink/40">
                          EVENT
                        </span>
                      </div>
                    )}
                  </div>

                  {/* テキスト (右) */}
                  <div className="flex-1 flex flex-col min-w-0">
                    {/* 日時 (赤カレンダーアイコン + date) */}
                    <div className="flex items-center gap-2 text-sm">
                      <svg
                        className="w-4 h-4 text-tora flex-shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 011 1v1h6V3a1 1 0 112 0v1h1a2 2 0 012 2v11a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zM4 8v9h12V8H4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-semibold text-tora-ink">
                        {date}
                      </span>
                      {time && (
                        <span className="text-tora-ink/60">{time}</span>
                      )}
                      <span className="text-tora-ink/40">/</span>
                      <span className="text-tora-ink/60">
                        {MODE_LABEL[e.participationMode] ??
                          e.participationMode}
                      </span>
                    </div>

                    {/* タイトル (大きめ bold) */}
                    <h3 className="mt-2 text-xl md:text-2xl font-bold text-tora-ink leading-snug group-hover:text-tora transition-colors">
                      {e.title}
                    </h3>

                    {/* サマリ */}
                    {e.summary && (
                      <p className="mt-1.5 text-sm md:text-base text-tora-ink/70 line-clamp-2">
                        {e.summary}
                      </p>
                    )}

                    {/* バッジ pill 群 */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {e.isAppliedByMe && (
                        <StatusBadge tone="accent" bold>
                          申込済み
                        </StatusBadge>
                      )}
                      {full ? (
                        <StatusBadge tone="danger" bold>
                          満席
                        </StatusBadge>
                      ) : e.remainingCapacity != null ? (
                        <StatusBadge tone="neutral">
                          残 {e.remainingCapacity} 席
                        </StatusBadge>
                      ) : null}
                      {e.capacityTotal != null && (
                        <StatusBadge tone="accent">
                          参加 {e.appliedCount ?? 0} / {e.capacityTotal}
                        </StatusBadge>
                      )}
                      {e.region && (
                        <StatusBadge tone="accent">
                          {regionLabel(e.region)}
                        </StatusBadge>
                      )}
                      {e.venueName && (
                        <StatusBadge tone="neutral">
                          {e.venueName}
                        </StatusBadge>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
